import React from 'react';

// Actions
import { GET_CONTACTS, getMyContacts, setPersistenceSource } from '@/redux/modules/contacts';
import { getOmniRiskProfiles } from '@/redux/modules/omni-risk-profiles';
import { startLoaderAction, selectIsLoadingEvery } from '@/redux/modules/ui/loaders';
import { ReduxState } from '@/redux/modules/types';
import { setIsActiveContactPostMessage } from '@/redux/modules/post-message';

// Components
import { SecondaryLink, SecondaryButton, PrimaryButton, CancelButton } from '../buttons';
import { ContactsTable } from '../tables';
import { RiskProfileCard } from '../common';
import { withErrorBoundary } from '@/views/components/errors';
import Modal from '@/views/components/modals/AccessibleModal';

// Enums
import { OmniRiskProfileStatus } from '@/enums/OmniRiskProfiles';

// Libraries
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { connect } from 'react-redux';

// Types
import AppMode from '@/types/app-mode';
import ContactRiskProfile from '@/types/contact-risk-profile';
import GenericContact from '@/types/generic-contact';
import Locale from '@/types/locale';
import User from '@/types/user';
import { ColumnSort } from '@tanstack/react-table';
import { ContactsTableFields } from '../tables/ContactsTable';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { SortingField } from '@/types/data';

// Utils
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { getTableSortedColumns } from '@/utils/data-table';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { SEARCH_DEBOUNCE_TIME } from '@/utils/debounce/constants';

type StateProps = {
  contacts: GenericContact[];
  nextContactsToken?: string;
  loadingContacts: boolean;
  omniRiskProfiles: FormattedFullOmniRiskProfile[];
  persistenceSource?: string;
  isPostMessageActive: boolean;
};

type DispatchProps = {
  getMyContacts: (...args: Parameters<typeof getMyContacts>) => Promise<GenericContact[]>;
  getOmniRiskProfiles: (id?: string) => FormattedFullOmniRiskProfile[];
  startLoaderAction: (name: string) => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

type Props = StateProps &
  DispatchProps & {
    show: boolean;
    locale: Locale;
    contacts: GenericContact[];
    currentContact?: GenericContact;
    onCancel: () => void;
    onSelect: (riskProfile: ContactRiskProfile | FormattedFullOmniRiskProfile) => void;
    user: User;
    isSaving?: boolean;
  };

type State = {
  contactsSortingFields: SortingField[];
  contactsSearchQuery: string;
  selectedContact?: GenericContact;
  selectedRiskProfile?: ContactRiskProfile | FormattedFullOmniRiskProfile;
  sortedColumns: ColumnSort[];
};

class SelectContactRiskProfile extends React.Component<Props, State> {
  readonly state: State = {
    selectedContact: this.props.currentContact,
    contactsSearchQuery: '',
    contactsSortingFields: [],
    sortedColumns: [],
  };

  debouncedSearchContacts = debounce(() => {
    const { contactsSearchQuery, contactsSortingFields } = this.state;
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields });
  }, SEARCH_DEBOUNCE_TIME);

  static defaultProps = {
    onSelect: () => {},
  };

  componentDidMount() {
    this.props.getMyContacts();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.show && !this.props.show) {
      this.setState({
        selectedContact: this.props.currentContact,
        selectedRiskProfile: undefined,
      });
    }
  }

  onContactsSortingChange = (contactsSortingFields: SortingField[]) => {
    const { contactsSearchQuery } = this.state;

    this.setState({ contactsSortingFields });
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields });
  };

  onContactsSearchChange = (contactsSearchQuery: string) => {
    this.setState({ contactsSearchQuery });
    this.props.startLoaderAction(GET_CONTACTS.type);

    this.debouncedSearchContacts();
  };

  onLoadMoreContacts = () => {
    const { contactsSearchQuery, contactsSortingFields } = this.state;
    const { nextContactsToken } = this.props;

    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: nextContactsToken, sort: contactsSortingFields });
  };

  onSelectContact = async (selectedContact: GenericContact) => {
    const { getOmniRiskProfiles, user } = this.props;

    if (userHasPermissionEnabled(user, PERMISSIONS.OMNI_RISK_PROFILE) && selectedContact._id) {
      await getOmniRiskProfiles(selectedContact._id);
    }

    if (selectedContact._id) {
      this.setState({ selectedContact });
    }
  };

  clearState = () => {
    this.setState({
      selectedContact: undefined,
      selectedRiskProfile: undefined,
    });
  };

  onConfirm = () => {
    const { selectedRiskProfile } = this.state;

    if (!selectedRiskProfile) {
      return;
    }

    this.props.onSelect(selectedRiskProfile);
  };

  selectProfile = (selectedRiskProfile: ContactRiskProfile | FormattedFullOmniRiskProfile) => {
    this.setState({ selectedRiskProfile });
  };

  renderRiskProfiles = () => {
    const { user } = this.props;
    if (userHasPermissionEnabled(user, PERMISSIONS.OMNI_RISK_PROFILE)) {
      return this.renderContactOmniRiskProfiles();
    }
    return this.renderContactRiskProfiles();
  };

  renderContactOmniRiskProfiles = () => {
    const { selectedContact, selectedRiskProfile } = this.state;
    const { locale, user, omniRiskProfiles } = this.props;
    const approvedOmniRiskProfiles = omniRiskProfiles.filter(
      (orp: FormattedFullOmniRiskProfile) => orp.contactId === selectedContact?._id && orp.status === OmniRiskProfileStatus.Approved && orp.profile?.id,
    );

    if (!approvedOmniRiskProfiles.length) {
      return (
        <div className="contact-no-profiles d-flex flex-column align-items-center flex-fill">
          <FormattedMessage id="SelectContactRiskProfile.NoProfiles" />

          <SecondaryButton onClick={this.clearState}>
            <FormattedMessage id="SelectContactRiskProfile.BackToContacts" />
          </SecondaryButton>
        </div>
      );
    }

    return (
      <PerfectScrollbar>
        {approvedOmniRiskProfiles.map((aorp: FormattedFullOmniRiskProfile) => {
          const { profile, requirements } = aorp;
          const classes = classNames({
            active: selectedRiskProfile?._id === aorp._id,
          });
          return (
            <RiskProfileCard
              key={profile!.id}
              className={classes}
              riskProfile={profile!}
              locale={locale}
              user={user}
              requirements={requirements}
              onSelect={() => this.selectProfile(aorp)}
            />
          );
        })}
      </PerfectScrollbar>
    );
  };

  renderContactRiskProfiles = () => {
    const { selectedContact, selectedRiskProfile } = this.state;
    const { locale, user } = this.props;

    if (!selectedContact) {
      return null;
    }

    if (!selectedContact.riskProfiles || selectedContact.riskProfiles.length === 0) {
      return (
        <div className="contact-no-profiles d-flex flex-column align-items-center flex-fill">
          <FormattedMessage id="SelectContactRiskProfile.NoProfiles" />

          <SecondaryButton onClick={this.clearState}>
            <FormattedMessage id="SelectContactRiskProfile.BackToContacts" />
          </SecondaryButton>
        </div>
      );
    }

    return (
      <PerfectScrollbar>
        {selectedContact.riskProfiles.map(profile => {
          const classes = classNames({
            active: selectedRiskProfile && selectedRiskProfile._id === profile._id,
          });

          return (
            <RiskProfileCard
              key={profile._id}
              className={classes}
              locale={locale}
              riskProfile={profile.profile}
              user={user}
              onSelect={() => this.selectProfile(profile)}
            />
          );
        })}
      </PerfectScrollbar>
    );
  };

  handleSorting = (sort: ColumnSort) => {
    const { contactsSearchQuery } = this.state;
    const { serverSortingParams, columnSort } = getTableSortedColumns(this.state.contactsSortingFields, sort);
    this.setState({ contactsSortingFields: serverSortingParams, sortedColumns: columnSort });
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: serverSortingParams });
  };

  render() {
    const {
      contacts,
      show,
      user,
      nextContactsToken,
      loadingContacts,
      isSaving,
      isPostMessageActive,
      onCancel,
      setIsActiveContactPostMessage,
      setPersistenceSource,
    } = this.props;
    const { selectedContact, selectedRiskProfile, contactsSearchQuery } = this.state;

    return (
      <Modal
        show={show}
        className={classNames('full-screen-md-modal modal-v2 select-contact-risk-profile-modal', { 'wider-modal': selectedContact })}
        onHide={onCancel}
        dialogClassName="modal-dialog-centered"
        backdropClassName="modal-v2-backdrop"
      >
        <Modal.Body className="d-flex flex-column">
          {!selectedContact && (
            <ContactsTable
              selectable={false}
              contacts={contacts}
              title={formatMessage('SelectContactRiskProfile.Title')}
              onClick={this.onSelectContact}
              user={user}
              loading={loadingContacts}
              searchQuery={contactsSearchQuery}
              hasMore={nextContactsToken != null}
              onLoadMore={this.onLoadMoreContacts}
              onSortingChange={this.onContactsSortingChange}
              onContactsSearch={this.onContactsSearchChange}
              debouncedSearchContacts={this.debouncedSearchContacts}
              orderedFields={[ContactsTableFields.Fname, ContactsTableFields.Lname, ContactsTableFields.Email]}
              showHeaderSection
              serverSideSorting
              onServerSideSorting={this.handleSorting}
              sortedColumns={this.state.sortedColumns}
              isPostMessageActive={isPostMessageActive}
              setPersistenceSource={setPersistenceSource}
              setIsActiveContactPostMessage={setIsActiveContactPostMessage}
            />
          )}

          {selectedContact && (
            <div className="risk-profiles-container d-flex flex-column flex-grow-1">
              <FormattedMessage tagName="h1" id="SelectContactRiskProfile.Title" />

              {selectedContact && user.appConfig.mode !== AppMode.CONTACT && (
                <SecondaryLink onClick={this.clearState} icon="arrow-left">
                  <FormattedMessage id="SelectContactRiskProfile.BackToContacts" />
                </SecondaryLink>
              )}

              <FormattedMessage tagName="h3" id="SelectContactRiskProfile.ContactProfiles" values={{ name: selectedContact.fname || selectedContact.email }} />

              {this.renderRiskProfiles()}
            </div>
          )}

          <div className="modal-actions d-flex justify-content-end">
            <CancelButton className="secondary-v2 cancel-button" onClick={onCancel} disabled={isSaving} />

            <PrimaryButton className="primary-v2" onClick={this.onConfirm} disabled={!selectedContact || !selectedRiskProfile} loading={isSaving}>
              <FormattedMessage id="SelectContactRiskProfile.SelectProfile" />
            </PrimaryButton>
          </div>
        </Modal.Body>
      </Modal>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => {
  const {
    omniRiskProfiles: { riskProfiles },
  } = state;

  return {
    contacts: state.contacts.contacts,
    persistenceSource: state.contacts.persistenceSource,
    nextContactsToken: state.contacts.nextContactsToken,
    loadingContacts: selectIsLoadingEvery(state, [GET_CONTACTS.type]),
    omniRiskProfiles: riskProfiles || [],
    isPostMessageActive: state.postMessage.isPostMessageActive,
  };
};
export default connect(mapStateToProps, {
  getMyContacts,
  getOmniRiskProfiles,
  startLoaderAction,
  setIsActiveContactPostMessage,
  setPersistenceSource,
})(withErrorBoundary(SelectContactRiskProfile));
